import { AaasAppSelectors } from '@va/aaas/shared/selectors';
import { moduleNames, tabNames } from '@va/constants';
import { getUserRole } from '@va/standalone/shared/selectors';
import { WithLink, WithSubMenu } from '@va/types/navigation';
import { isEcomModuleEnabled } from '@va/util/helpers';
import { GROWTH_ENGINE_EVENT_CATEGORY, trackGrowthEngineClick } from '@va/util/misc';
import { Link } from '@va/util/router';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const useNavigationTabs = (isModuleEnabled: (tabName: string) => boolean) => {
  const userRole = useSelector(getUserRole);
  const isWebsiteSubscriptionModuleEnabled = useSelector(AaasAppSelectors.selectIsWebsiteSubscriptionModuleEnabled);

  const feedbackTab: WithSubMenu = useMemo(
    () => ({
      index: 5,
      name: tabNames.feedback,
      hasSubGroups: true,
      displayInLeftNav: isModuleEnabled(moduleNames.feedback),
      submenu: [
        {
          index: 5.1,
          name: tabNames.polls,
          linkTo: ({ className }) => <Link className={className} to={'/feedback/polls'} />,
          displayInLeftNav: isModuleEnabled(moduleNames.polls),
        },
        {
          index: 5.2,
          name: tabNames.surveys,
          linkTo: ({ className }) => <Link className={className} to={'/feedback/surveys'} />,
          displayInLeftNav: isModuleEnabled(moduleNames.surveys),
        },
      ],
    }),
    [isModuleEnabled],
  );

  const ecommerceTabs: WithSubMenu = useMemo(
    () => ({
      name: tabNames.ecommerce,
      displayInLeftNav: isEcomModuleEnabled(userRole),
      submenu: [
        {
          linkTo: ({ className }) => <Link className={className} to={`/ecommerce/overview`} />,
          name: tabNames.ecommerceOverview,
          displayInLeftNav: true,
        },
        {
          linkTo: ({ className }) => <Link className={className} to={`/ecommerce/customers`} />,
          name: tabNames.ecommerceCustomers,
          displayInLeftNav: true,
        },
        {
          linkTo: ({ className }) => <Link className={className} to={`/ecommerce/orders`} />,
          name: tabNames.ecommerceOrders,
          displayInLeftNav: false,
        },
        {
          linkTo: ({ className }) => <Link className={className} to={`/ecommerce/sales-charts`} />,
          name: tabNames.ecommerceSalesCharts,
          displayInLeftNav: true,
        },
      ],
    }),
    [userRole],
  );

  const tabs: Array<WithLink | WithSubMenu> = useMemo(
    () => [
      {
        index: 1,
        linkTo: ({ className }) => <Link className={className} to={'/'} />,
        name: tabNames.overview,
        displayInLeftNav: isModuleEnabled(moduleNames.mainDashboards),
      },
      ecommerceTabs,
      {
        index: 2,
        name: tabNames.webStatsOverview,
        hasSubGroups: true,
        displayInLeftNav: isModuleEnabled(moduleNames.webStatsModule),
        submenu: [
          {
            index: 2.2,
            name: tabNames.trafficStructure,
            linkTo: ({ className }) => <Link className={className} to={`/traffic/share`} />,
            displayInLeftNav: isModuleEnabled(moduleNames.trafficStructure),
            children: [
              {
                name: tabNames.trafficShare,
                linkTo: `/traffic/share`,
                displayInLeftNav: false,
              },
              {
                name: tabNames.trafficSession,
                linkTo: `/traffic/session`,
                displayInLeftNav: false,
              },
              {
                name: tabNames.trafficPagesPerVisit,
                linkTo: `/traffic/visits`,
                displayInLeftNav: false,
              },
              {
                name: tabNames.trafficBounceRate,
                linkTo: `/traffic/bounce-rate`,
                displayInLeftNav: false,
              },
              {
                name: tabNames.internalTraffic,
                linkTo: `/traffic/internal-traffic`,
                displayInLeftNav: false,
              },
            ],
          },
          {
            index: 2.3,
            name: tabNames.outgoingTraffic,
            linkTo: ({ className }) => <Link className={className} to={`/outgoing-traffic`} />,
            displayInLeftNav: isModuleEnabled(moduleNames.outgoingTraffic),
          },
          {
            index: 2.35,
            name: tabNames.metadataAnalytics,
            linkTo: ({ className }) => <Link className={className} to={'/content-analysis'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.metadataAnalytics),
          },
          {
            index: 2.4,
            name: tabNames.conversionEvents,
            linkTo: ({ className }) => <Link className={className} to={`/conversion-events`} />,
            displayInLeftNav: isModuleEnabled(moduleNames.conversionEvents),
          },
          {
            index: 2.5,
            name: tabNames.latestVisitorsList,
            linkTo: ({ className }) => <Link className={className} to={'/visitors/latest'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.visitors),
            children: [
              {
                name: tabNames.latestVisitorsList,
                linkTo: '/visitors/latest',
                displayInLeftNav: false,
              },
              {
                name: tabNames.latestVisitorsMap,
                linkTo: '/visitors/map',
                displayInLeftNav: false,
              },
              {
                name: tabNames.trafficCharts,
                linkTo: '/visitors/charts',
                displayInLeftNav: false,
              },
              {
                name: tabNames.latestVisitorsLocations,
                linkTo: '/visitors/locations',
                displayInLeftNav: false,
              },
            ],
          },
          {
            index: 2.6,
            linkTo: ({ className }) => <Link className={className} to={'/pages/landing'} />,
            name: tabNames.pageVisits,
            displayInLeftNav: isModuleEnabled(moduleNames.pages),
            children: [
              {
                name: tabNames.landingPages,
                linkTo: '/pages/landing',
                displayInLeftNav: false,
              },
              {
                name: tabNames.conversions,
                linkTo: '/pages/conversions',
                displayInLeftNav: false,
              },

              {
                name: tabNames.referringSites,
                linkTo: '/pages/referring',
                displayInLeftNav: false,
              },
              {
                name: tabNames.internalURLReferrers,
                linkTo: '/pages/internal-referring',
                displayInLeftNav: false,
              },
            ],
          },
          {
            index: 2.7,
            linkTo: ({ className }) => <Link className={className} to={'/competition'} />,
            name: tabNames.competition,
            displayInLeftNav: isModuleEnabled(moduleNames.competition),
          },
          {
            index: 2.8,
            linkTo: ({ className }) => <Link className={className} to={'/devices'} />,
            name: tabNames.devices,
            displayInLeftNav: isModuleEnabled(moduleNames.devices),
          },
          {
            index: 2.9,
            name: tabNames.campaignsOverview,
            linkTo: ({ className }) => <Link className={className} to={'/campaigns/overview'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.utmOverview),
            children: [
              {
                name: tabNames.adsOverview,
                linkTo: '/campaigns/overview',
                displayInLeftNav: false,
              },
              {
                name: tabNames.adsLatestVisitors,
                linkTo: '/campaigns/latest',
                displayInLeftNav: false,
              },
              {
                name: tabNames.myCampaigns,
                linkTo: '/campaigns/my',
                displayInLeftNav: false,
              },
              {
                name: tabNames.adsCharts,
                linkTo: '/campaigns/charts',
                displayInLeftNav: false,
              },
              {
                name: tabNames.utmGenerator,
                linkTo: `/campaigns/utm-generator`,
                displayInLeftNav: false,
              },
            ],
          },
          {
            index: 3,
            name: tabNames.websiteErrors,
            linkTo: ({ className }) => <Link className={className} to={'/website-errors'} />,
            // TODO Fix properly using the getFeaturePermissions
            displayInLeftNav: false, // isModuleEnabled(moduleNames.websiteErrors),
          },
        ],
      },
      {
        index: 4,
        name: tabNames.behaviour,
        displayInLeftNav: isModuleEnabled(moduleNames.visitorBehavior),
        submenu: [
          {
            name: tabNames.eventTracking,
            linkTo: ({ className }) => <Link className={className} to={'/behaviour/event-tracking'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.eventTracking),
          },
          {
            name: tabNames.recordings,
            linkTo: ({ className }) => <Link className={className} to={'/behaviour/recordings'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.sessionRecordings),
          },
          {
            name: tabNames.heatmaps,
            linkTo: ({ className }) => <Link className={className} to={'/behaviour/heatmaps'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.heatmaps),
          },
          {
            name: tabNames.funnels,
            linkTo: ({ className }) => <Link className={className} to={'/behaviour/funnels'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.funnels),
          },
        ],
      },
      feedbackTab,
      {
        index: 5.5,
        name: tabNames.trendIntel,
        linkTo: ({ className }) => (
          <Link
            className={className}
            to={'/growth-engine'}
            onClick={() => trackGrowthEngineClick(GROWTH_ENGINE_EVENT_CATEGORY.mainNavigation)}
          />
        ),
        isAlpha: true,
        displayInLeftNav: isModuleEnabled(moduleNames.trendIntel),
      },
      {
        index: 6,
        name: tabNames.privacyCenter,
        linkTo: ({ className }) => <Link className={className} to={'/settings/privacy-center'} />,
        displayInLeftNav: isModuleEnabled(moduleNames.privacyCenter),
      },
      {
        index: 7,
        name: tabNames.websiteSettings,
        displayInLeftNav: isModuleEnabled(moduleNames.websiteSettings),
        submenu: [
          {
            name: tabNames.settings,
            linkTo: ({ className }) => <Link className={className} to={'/settings'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.generalSettings),
          },
          {
            name: tabNames.conversionsSettings,
            linkTo: ({ className }) => <Link className={className} to={'/settings/conversions'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.conversionsSettings),
          },
          {
            name: tabNames.modulesSettings,
            linkTo: ({ className }) => <Link className={className} to={'/settings/modules'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.moduleSettings),
          },
          {
            name: tabNames.subscriptionSettings,
            linkTo: ({ className }) => <Link className={className} to={'/settings/subscription'} />,
            displayInLeftNav: isWebsiteSubscriptionModuleEnabled,
          },
          {
            name: tabNames.pageSettings,
            linkTo: ({ className }) => <Link className={className} to={'/settings/pages'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.specialPages),
          },
          {
            name: tabNames.visitorSegments,
            linkTo: ({ className }) => <Link className={className} to={'/settings/visitor-segments'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.segments),
          },
          {
            name: tabNames.consent,
            linkTo: ({ className }) => <Link className={className} to={'/settings/consent'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.consent),
          },
          {
            name: tabNames.export,
            linkTo: ({ className }) => <Link className={className} to={'/export'} />,
            displayInLeftNav: isModuleEnabled(moduleNames.export),
          },
        ],
      },
    ],
    [ecommerceTabs, feedbackTab, isModuleEnabled, isWebsiteSubscriptionModuleEnabled],
  );

  return tabs;
};
